<template>
    <div
        class="icon-label"
        :class="{
            [`--${size}`]: true,
            '--icon-only': !showLabel || !label,
            '--label-only': !showIcon,
            '--has-label': showLabel && label,
        }">
        <pendo-loading-indicator
            v-if="loadingIcon"
            type="skeleton"
            :skeleton-props="{
                width: formattedIcon.size,
                height: formattedIcon.size,
            }" />
        <img
            v-if="!loadingIcon && showIcon && imgSrc"
            v-pendo-tooltip="tooltip"
            :height="formattedIcon.size"
            :width="formattedIcon.size"
            :alt="label"
            :src="imgSrc">
        <pendo-icon
            v-if="!loadingIcon && showIcon && icon"
            v-pendo-tooltip="tooltip"
            :size="formattedIcon.size"
            :alt="label"
            :type="iconObj.type"
            :fill="iconObj.fill || 'none'"
            :stroke="iconObj.stroke || 'currentColor'"
            :stroke-width="iconObj.strokeWidth || undefined"
            :style="formattedIcon.style" />
        <span
            v-if="showLabel && label"
            ref="label"
            v-pendo-tooltip="{
                content: truncatedTooltip,
                multiline: true,
                arrow: true,
            }"
            @mouseover="onMouseOver">{{ label }}</span>
    </div>
</template>

<script setup>
import { PendoIcon, PendoLoadingIndicator, PendoTooltip as vPendoTooltip } from '@pendo/components';
import { computed, ref, unref, useTemplateRef } from 'vue';

const { icon, imgSrc, label, size, showLabel } = defineProps({
    imgSrc: {
        type: String,
        default: '',
    },
    icon: {
        type: [String, Object],
        default: '',
    },
    label: {
        type: String,
        default: '',
    },
    showIcon: {
        type: Boolean,
        default: false,
    },
    showLabel: {
        type: Boolean,
        default: false,
    },
    size: {
        type: String,
        default: 'large',
        validator: (value) => ['small', 'medium', 'large', 'xlarge'].includes(value),
    },
    loadingIcon: {
        type: Boolean,
        default: false,
    },
});

const tooltip = computed(() => (showLabel && label ? { content: null } : label));
const iconObj = computed(() => (typeof icon === 'string' ? { type: icon } : icon));

const formattedIcon = computed(() => {
    const iconObjValue = unref(iconObj);
    const sizeSuffix = imgSrc ? 'px' : '';
    const hasPadding = iconObjValue.borderColor || iconObjValue.backgroundColor;

    let formattedSize = 24;
    let padding = 4;

    switch (size) {
        case 'small':
            formattedSize = 12;
            padding = 2;
            break;
        case 'medium':
            formattedSize = 18;
            padding = 2;
            break;
        case 'xlarge':
            formattedSize = 30;
            padding = 6;
            break;
    }

    if(iconObjValue.sizePercentage) {
        formattedSize = formattedSize * iconObjValue.sizePercentage / 100;
    }

    if (hasPadding) {
        formattedSize -= padding * 2;
    }
    if (iconObjValue.borderColor) {
        formattedSize -= 2;
    }

    return {
        size: `${formattedSize}${sizeSuffix}`,
        style: {
            ...(iconObjValue.color && { color: iconObjValue.color }),
            ...(iconObjValue.borderColor && {
                border: `1px solid ${iconObjValue.borderColor}`,
                padding: `${padding}px`,
            }),
            ...(iconObjValue.backgroundColor && {
                backgroundColor: iconObjValue.backgroundColor,
                padding: `${padding}px`,
            }),
        },
    };
});

const labelRef = useTemplateRef('label');
const truncatedTooltip = ref('');

function checkTruncation () {
    const el = unref(labelRef);
    if (!el) return false;

    return el.scrollWidth > el.clientWidth;
}

const onMouseOver = () => {
    if(checkTruncation()) {
        truncatedTooltip.value = label;
    } else {
        truncatedTooltip.value = '';
    }
};
</script>

<style scoped lang="less">
.icon-label {
    &.--has-label {
        display: flex;
        align-items: center;
        overflow: hidden;

        img {
            flex-grow: 0;
            flex-shrink: 0;
            border-radius: 3px;
        }

        :deep(.pendo-icon) {
            flex-grow: 0;
            flex-shrink: 0;
        }
        :deep(.pendo-icon svg) {
            border-radius: 3px;
        }

        span {
            color: var(--palette-gray-90);
            max-width: 100%;
            flex-grow: 1;
            flex-shrink: 1;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            line-height: 150%;
        }
    }

    :deep(.pendo-icon svg) {
        border-radius: 3px;
    }

    &.--small,
    &.--medium {
        gap: 4px;
        font-size: 12px;
    }

    &.--large {
        gap: 8px;
        font-size: 14px;
    }

    &.--xlarge {
        gap: 8px;
        font-size: 16px;
    }
}
</style>
